import { PortalUserProfile } from '@amzn/awscat-pattern-library-service-model';
import axios, { AxiosInstance, AxiosPromise } from 'axios';
import { isEmail } from 'class-validator';
import queryString from 'query-string';
import EnvironmentConfig from '../common/EnvironmentConfig';
import JSONSerializer from '../common/JSONSerializer';
import LocalStorage from '../common/LocalStorage';
import { flashbar } from '../decorators/flashbar';
import GetJwksResponse from '../model/GetJwksResponse';
import UserProfile from '../model/UserProfile';
import { localLog } from '../loggers/Logger';

class PortalServiceClient {
  // Singleton. Should be replaced with proper IoC container
  public static create(): PortalServiceClient {
    if (!PortalServiceClient.instance) {
      PortalServiceClient.instance = new PortalServiceClient();
    }

    return PortalServiceClient.instance;
  }

  private static instance: PortalServiceClient;
  serializer: JSONSerializer;

  private readonly axios: AxiosInstance;
  private userProfileCache: Map<string, Promise<UserProfile>>;

  private constructor() {
    this.axios = axios.create({
      baseURL: EnvironmentConfig.getConfig().portalEndpoint,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      withCredentials: true,
      timeout: 120000,
    });

    this.userProfileCache = new Map<string, Promise<UserProfile>>();
    this.serializer = new JSONSerializer();
  }

  public getUserProfiles = async (userIds: string[]): Promise<UserProfile[]> => {
    if (!Array.isArray(userIds)) {
      throw new Error('userIds is not an array');
    }

    const result: Array<Promise<UserProfile>> = [];
    for (const [idx, userId] of Array.from(userIds.entries())) {
      if (this.userProfileCache.has(userId)) {
        const userProfile = this.userProfileCache.get(userId);
        if (userProfile) {
          result.push(userProfile);
          userIds.splice(idx, 1);
        }
      }
    }

    if (userIds.length === 0) {
      return await Promise.all(result);
    }

    const portalCall = this.axios.get(`/users?userIds=${userIds.join(',')}`);

    const getResultByUserId = async (call: AxiosPromise<any>, refId: string) => {
      const response = await call;
      const userProfiles: UserProfile[] = response.data;
      return userProfiles.find((up) => up.userId === refId);
    };

    // Stores promise of service call response in cache to avoid making concurrent requests for same user ID
    for (const userId of userIds) {
      const resultPromise = getResultByUserId(portalCall, userId);
      // @ts-expect-error TS2345 - strict migration
      this.userProfileCache.set(userId, resultPromise);
      // @ts-expect-error TS2345 - strict migration
      result.push(resultPromise);
    }

    return await Promise.all(result);
  };

  @flashbar({ error: 'Failed to fetch user profile for signed-in user.' })
  public async getSignedInUserProfile() {
    const res = await this.axios.get('/users/current');
    return this.serializer.deserialize<PortalUserProfile>(res.data, PortalUserProfile);
  }

  @flashbar({ error: 'Failed to fetch JWKS.' })
  public async getJWKS() {
    const res = await this.axios.get('/jwks');
    // Don't use json2typescript serializer as return value needs to be Object for compatibility with jose
    return res.data as GetJwksResponse;
  }

  @flashbar({ error: 'Failed to refresh authentication token.' })
  public async getAuthenticationToken(): Promise<string> {
    const qs = this.createTokenRequestQueryString();
    const res = await this.axios.get(`/sso?${qs}`);
    return res.data;
  }

  public redirectToLoginPage(preLoginRoute?: string) {
    const qs = this.createTokenRequestQueryString(preLoginRoute);
    window.location.replace(`${EnvironmentConfig.getConfig().portalEndpoint}/sso?${qs}`);
  }

  public async searchUserProfile(
    searchTerm: string,
  ): Promise<Array<{ userId: string; name: string; type: string }>> {
    const queryParams = queryString.stringify({ searchTerm });
    const response = await this.axios.get(`/users?${queryParams}`);

    // @ts-expect-error TS7006 - strict migration
    return response.data.map((up) => ({
      userId: up.userId,
      name: up.name,
      type: up.type,
      email: up.email,
    }));
  }

  public async searchUserProfileByEmail(
    email: string,
  ): Promise<Array<{ userId: string; name: string; type: string }>> {
    if (isEmail(email)) {
      const queryParams = queryString.stringify({ email });
      const response = await this.axios.get(`/users?${queryParams}`);
      // @ts-expect-error TS7006 - strict migration
      return response.data.map((up) => ({
        userId: up.userId,
        name: up.name,
        type: up.type,
        email: up.email,
      }));
    }
    return [];
  }

  private createTokenRequestQueryString(preLoginRoute?: string) {
    const hostname = window && window.location && window.location.hostname;
    const port = location && location.port ? `:${location.port}` : '';
    const redirectUri = `https://${hostname}${port}/login`;
    const preLoginRoutePrefix = ';PRE_LOGIN_ROUTE=';

    let state = Array.from(window.crypto.getRandomValues(new Uint8Array(30)))
      .map((b) => b.toString(36).padStart(2, '0'))
      .join('');
    if (preLoginRoute) {
      state = `${state}${preLoginRoutePrefix}${preLoginRoute}`;
    }
    const encodedState = encodeURIComponent(state);
    LocalStorage.setAuthState(encodedState);
    const queryParams = {
      appId: 'mpl',
      redirectUri,
      state: encodedState,
    };
    const qs = queryString.stringify(queryParams);
    return qs;
  }
}

export default PortalServiceClient;
